<script async setup>
import { defineProps, computed, ref, onMounted, onBeforeUnmount, watch } from "vue";
import KdButton from "./KdButton.vue";
import KdIcon from "./KdIcon.vue";
import FavoritesService, { FAVORITES_EVENT } from "../services/FavoritesService.js";

const props = defineProps({
    productData: {
        type: Object,
        default: () => ({}),
    },
    variant: {
        type: String,
        default: "",
    },
});

const data = computed(() => props.productData || {});

const currentVariant = computed(
    () => props.variant || data.value.variant || "small"
);

const shortDescription = computed(() => data.value.shortDescription || "");

const isSmall  = computed(() => currentVariant.value === "small");
const isBig    = computed(() => currentVariant.value === "big");
const isSearch = computed(() => currentVariant.value === "search");

const showAllColors = ref(false);

const colors = computed(() => {
    const raw = data.value.colors;
    return Array.isArray(raw) ? raw : [];
});

const hasMoreColors = computed(() => colors.value.length > 6);

const visibleColors = computed(() =>
    showAllColors.value ? colors.value : colors.value.slice(0, 6)
);

const hasActiveVariation = computed(
    () => !!(data.value.isVariable && data.value.activeVariationId)
);

const activeColorSlug = computed(() => {
    const attrs = data.value.activeAttrsRaw || {};
    return attrs.pa_farbe || "";
});

const favoriteId = computed(() => {
    if (data.value.isVariable && data.value.activeVariationId) {
        return Number(data.value.activeVariationId);
    }
    return Number(data.value.id || 0);
});

const hasBadge = computed(() => {
    if (isBig.value || isSearch.value) {
        return !!data.value.badgeText;
    }
    return !!(data.value.badge?.text || data.value.price?.discount);
});

const badgeLabel = computed(() => {
    if (isBig.value || isSearch.value) {
        return data.value.badgeText || "";
    }

    if (data.value.badge?.text) {
        return data.value.badge.text;
    }

    const discount = data.value.price?.discount;
    if (discount === undefined || discount === null || discount === "") {
        return "";
    }

    return `-${discount}%`;
});

const isFavorite = ref(false);
const isHover = ref(false);

const updateFavoriteFromService = () => {
    const id = favoriteId.value;
    if (!id) {
        isFavorite.value = false;
        return;
    }
    isFavorite.value = FavoritesService.isFavorite(id);
};

const onFavoritesChanged = () => {
    updateFavoriteFromService();
};

onMounted(() => {
    updateFavoriteFromService();

    if (typeof window !== "undefined") {
        window.addEventListener(FAVORITES_EVENT, onFavoritesChanged);
    }
});

onBeforeUnmount(() => {
    if (typeof window !== "undefined") {
        window.removeEventListener(FAVORITES_EVENT, onFavoritesChanged);
    }
});

watch(favoriteId, () => {
    updateFavoriteFromService();
});
</script>

<template>
    <div
        class="relative h-full flex bg-white rounded-b-xl transition-transform duration-200"
        :class="[
            isSearch ? 'flex-row hover:scale-[1.02] shadow-ts-search cursor-pointer' : 'flex-col shadow-md'
        ]"
    >
        <a
            v-if="isSearch"
            :href="data.url"
            :title="data.title"
            :aria-label="data.title"
            class="absolute inset-0 z-10"
        ></a>
        <!-- search: eigene linke Spalte -->
        <div
            v-if="isSearch"
            class="relative w-23 shrink-0"
        >
            <div
                v-if="data.image && data.image.url"
                class="block w-full aspect-square overflow-hidden bg-white"
            >
                <img
                    :src="data.image.url"
                    :alt="data.image.alt || data.title"
                    class="size-full object-cover"
                >
            </div>
            <div
                v-if="hasBadge"
                class="absolute top-[85%] bg-black text-white text-xs rounded-r-md px-4 py-1"
            >
                {{ badgeLabel }}
            </div>
        </div>

        <!-- small / big: Bild oben -->
        <div v-else class="w-full">
            <div class="relative">
                <a
                    v-if="data.image && data.image.url"
                    :href="data.url"
                    :title="data.title"
                    class="block aspect-square overflow-hidden rounded-t-xl bg-white"
                >
                    <img
                        :src="data.image.url"
                        :alt="data.image.alt || data.title"
                        class="size-full object-cover"
                    >
                </a>
                <div
                    v-if="hasBadge"
                    class="absolute top-[85%] bg-black text-white text-xs rounded-r-md px-4 py-1"
                >
                    {{ badgeLabel }}
                </div>
            </div>
        </div>

        <!-- CONTENT RECHTS (oder unter dem Bild bei small/big) -->
        <div class="flex flex-col py-2 px-4 gap-3">
            <!-- Brand + Herz NUR bei small -->
            <div
                v-if="isSmall"
                class="flex flex-row justify-between items-start relative"
            >
                <div
                    v-if="props.productData.brand?.logo?.url"
                    class="w-16"
                >
                    <img
                        :src="props.productData.brand.logo.url"
                        :alt="props.productData.brand.logo.alt"
                    >
                </div>
                <div 
                    class="group absolute top-0 right-0"
                    @mouseenter="isHover = true"
                    @mouseleave="isHover = false"
                >
                    <kd-icon
                        icon="heart"
                        :filled="isFavorite || isHover"
                        size="20"
                        class="transition-transform duration-200 cursor-pointer hover:-translate-y-0.5"
                        @click="isFavorite = FavoritesService.toggle(favoriteId)"
                    />
                </div>
            </div>

            <template v-if="isSearch">
                <div class="text-sm leading-tight line-clamp-1 max-w-44">
                    {{ props.productData.title }}
                </div>
            </template>
            <template v-else>
                <a
                    :href="props.productData.url"
                    :title="props.productData.title"
                    class="text-sm max-w-44"
                >
                    {{ props.productData.title }}
                </a>
            </template>
            <!-- Farbbubbles nur bei small -->
            <div
                v-if="isSmall"
                class="flex items-center gap-1 flex-wrap"
            >
                <template v-for="(color, index) in visibleColors" :key="index">
                    <a
                        :href="color.url"
                        class="p-0.5 rounded-full border transition-all duration-200 flex items-center justify-center"
                        :class="activeColorSlug
                            ? (color.slug === activeColorSlug
                                ? 'border-black'
                                : 'border-gray-300 hover:border-black')
                            : (index === 0
                                ? 'border-black'
                                : 'border-gray-300 hover:border-black')"
                        :title="color.name"
                    >
                        <span
                            class="size-3 rounded-full block"
                            :style="{
                                background: color.hex2
                                    ? `linear-gradient(135deg, ${color.hex} 50%, ${color.hex2} 50%)`
                                    : color.hex
                            }"
                        ></span>
                    </a>
                </template>
                <div
                    v-if="hasMoreColors && !showAllColors"
                    class="flex items-center gap-1 cursor-pointer"
                    @click="showAllColors = true"
                >
                    <kd-icon-plus fill="black" size="20" />
                    <span class="text-xs text-black duration-200 leading-none">
                        {{ props.productData.moreColorsLabel }}
                    </span>
                </div>
            </div>
            

            <!-- variantLine + ShortDescription bei big UND search -->
            <div
                v-if="isBig || (isSearch && props.productData.variantLine)"
                class="flex flex-col gap-3"
            >
                <div class="text-xs">
                    <span v-if="props.productData.variantLine">{{ props.productData.variantLine }}</span>
                </div>
            
                <div
                    v-if="!isSearch && shortDescription"
                    class="flex items-start gap-1 text-xs"
                >
                    <kd-icon-checkmark class="shrink-0" size="20" fill="black" />
                    {{ shortDescription }}
                </div>
            </div>

            <!-- Bottom-Bereich -->
            <div :class="{'mt-auto': !isSearch}">
                <!-- small: alter Bottom-Block -->
                <div
                    v-if="isSmall"
                    class="flex flex-row items-center relative"
                >
                    <div class="flex flex-row gap-2">
                        <div
                            v-if="props.productData.price?.regularText"
                            class="line-through text-black/60 decoration-black decoration-2 content-center"
                        >
                            {{ props.productData.price.regularText }}
                        </div>
                        <div class="content-center">{{ props.productData.price?.saleText }}</div>
                    </div>

                    <form
                        method="post"
                        class="js-add-to-cart-form flex items-center gap-3 absolute bottom-0 right-0"
                        :data-product-id="props.productData.id"
                        @submit.prevent
                    >
                        <input
                            type="hidden"
                            name="product_id"
                            :value="props.productData.id"
                        >
                                
                        <template v-if="hasActiveVariation">
                            <input
                                type="hidden"
                                name="variation_id"
                                :value="props.productData.activeVariationId"
                            >
                            <input
                                v-for="(slug, tax) in props.productData.activeAttrsRaw || {}"
                                :key="tax"
                                type="hidden"
                                :name="`attribute_${tax}`"
                                :value="slug"
                            >
                        </template>
                    
                        <input type="hidden" name="add-to-cart" :value="props.productData.id">
                    
                        <button
                            type="submit"
                            class="transition-transform duration-200 hover:-translate-y-0.5"
                            :title="`add-to-cart ${props.productData.title}`"
                            aria-label="add-to-cart"
                        >
                            <kd-icon
                                icon="cart-plus"
                                fill="current"
                                :size="35"
                                class=" hover:text-white hover:drop-shadow-ts-tight-black"
                            />
                        </button>
                    </form>
                </div>

                <!-- big + search: gleicher Bottom-Block -->
                <div
                    v-else-if="isBig || isSearch"
                    class=""
                >
                    <div class="flex flex-row gap-2 flex-wrap">
                        <div
                            v-if="props.productData.price?.regularText"
                            :class="[
                                'line-through text-black/60 decoration-black decoration-2 content-center',
                                isSearch ? 'hidden lg:block' : ''
                            ]"
                        >
                            {{ props.productData.price.regularText }}
                        </div>
                        <div class="content-center">{{ props.productData.price?.saleText }}</div>
                    </div>

                    <div class="flex flex-row items-center gap-2">
                        <div 
                            v-if="isBig"
                            class="group"
                            @mouseenter="isHover = true"
                            @mouseleave="isHover = false"
                        >
                            <kd-icon
                                icon="heart"
                                :filled="isFavorite || isHover"
                                size="20"
                                class="transition-transform duration-200 cursor-pointer hover:-translate-y-0.5"
                                @click="isFavorite = FavoritesService.toggle(favoriteId)"
                            />
                        </div>

                        <form
                            method="post"
                            class="js-add-to-cart-form flex items-center"
                            :data-product-id="props.productData.id"
                            @submit.prevent
                        >
                            <input type="hidden" name="product_id" :value="props.productData.id">
                                            
                            <template v-if="hasActiveVariation">
                                <input
                                    type="hidden"
                                    name="variation_id"
                                    :value="props.productData.activeVariationId"
                                >
                                <input
                                    v-for="(slug, tax) in props.productData.activeAttrsRaw || {}"
                                    :key="tax"
                                    type="hidden"
                                    :name="`attribute_${tax}`"
                                    :value="slug"
                                >
                            </template>
                        
                            <input type="hidden" name="add-to-cart" :value="props.productData.id">
                        
                            <button
                                type="submit"
                                class="transition-transform duration-200 hover:-translate-y-0.5"
                                :title="`add-to-cart ${props.productData.title}`"
                                aria-label="add-to-cart"
                            >
                                <kd-icon
                                    v-if="!isSearch"
                                    icon="cart-plus"
                                    fill="current"
                                    :size="35"
                                    class="hover:text-white hover:drop-shadow-ts-tight-black"
                                />
                            </button>
                        </form>
                    </div>

                    <KdButton
                        v-if="isBig"
                        class="mt-4"
                        :href="props.productData.url"
                        :icon="props.productData.icon"
                        variant="black"
                        :title="props.productData.title"
                    >
                        {{ currentVariant === 'search'
                            ? props.productData.buttonLabelSearch || props.productData.buttonLabel
                            : props.productData.buttonLabel
                        }}
                    </KdButton>
                </div>
            </div>
        </div>
    </div>
</template>